import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import { TQC2070_R01Dto } from './Dto_TQC2070_R01';
import { formatDate } from 'src/utils/string/stringUtil';
import { extractDayFromAnalysisDt } from './Utils_TQC2070_R01';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);

/**
 * 차트 설정 상수
 */
const CHART_CONFIG = {
	znAm: {
		title: '아연 액분석 (AM 09:00)',
		field: 'znAm' as keyof TQC2070_R01Dto,
		color: '#3B82F6', // Blue
	},
	znPm: {
		title: '아연 액분석 (PM 01:00)',
		field: 'znPm' as keyof TQC2070_R01Dto,
		color: '#22C55E', // Green
	},
	naohAm: {
		title: '가성소다 액분석 (AM 09:00)',
		field: 'naohAm' as keyof TQC2070_R01Dto,
		color: '#FB923C', // Orange
	},
	naohPm: {
		title: '가성소다 액분석 (PM 01:00)',
		field: 'naohPm' as keyof TQC2070_R01Dto,
		color: '#A855F7', // Purple
	},
} as const;

/**
 * 차트 스타일 상수
 */
const CHART_CONSTANTS = {
	// Y축 범위 계산
	PADDING_RATIO: 0.1,           // Y축 패딩 비율 (10%)
	DEFAULT_PADDING: 1,            // 기본 패딩 값
	MIN_Y_VALUE: 0,                // 최소 Y값
	
	// 선 스타일
	LINE_TENSION: 0.1,             // 선 곡률
	POINT_RADIUS: 3,               // 포인트 반지름
	POINT_HOVER_RADIUS: 6,         // 호버 시 포인트 반지름
	POINT_BORDER_WIDTH: 1.5,       // 포인트 테두리 두께
	BACKGROUND_OPACITY: 0.1,       // 배경 투명도
	
	// 폰트 크기
	TITLE_FONT_SIZE: 14,
	AXIS_FONT_SIZE: 12,
	TICK_FONT_SIZE: 11,
	
	// 기타
	MAX_TICKS_LIMIT: 15,           // X축 최대 눈금 개수
	CHART_HEIGHT: '280px',         // 차트 높이
} as const;

interface ChartProps {
	data: TQC2070_R01Dto[];
}

export const Chart_TQC2070_R01: React.FC<ChartProps> = ({ data }) => {
	const chartConfigs = useMemo(() => {
		if (!data || data.length === 0) {
			return [];
		}

		// 데이터를 날짜순으로 정렬 (yyyy-MM-dd 형식은 문자열 비교로 정렬 가능)
		const sortedData = [...data].sort((a, b) => {
			return a.analysisDt.localeCompare(b.analysisDt);
		});

		// X축 라벨을 1일부터 31일까지 고정으로 생성
		const labels = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

		// 차트 설정을 기반으로 차트 데이터 생성
		const charts = Object.entries(CHART_CONFIG).map(([key, config]) => {
			// 1일부터 31일까지의 데이터 배열 생성
			const chartData = labels.map((day) => {
				// 해당 일자에 맞는 데이터 찾기
				const matchingItem = sortedData.find(item => {
					const itemDay = extractDayFromAnalysisDt(item.analysisDt);
					return itemDay === day;
				});
				
				if (matchingItem) {
					const value = matchingItem[config.field];
					return typeof value === 'number' ? value : null;
				}
				return null; // 데이터가 없는 날짜는 null
			});

			return {
				title: config.title,
				field: config.field,
				data: chartData,
				color: config.color,
			};
		});

		return charts.map((chart, index) => {
			// Y축 범위 계산
			const values = chart.data.filter((v): v is number => v !== null && !isNaN(v));
			const maxValue = values.length ? Math.max(...values) : 0;
			const minValue = values.length ? Math.min(...values) : 0;
			const padding = (maxValue - minValue) * CHART_CONSTANTS.PADDING_RATIO || CHART_CONSTANTS.DEFAULT_PADDING;
			const yMax = maxValue + padding;
			const yMin = Math.max(CHART_CONSTANTS.MIN_Y_VALUE, minValue - padding);

			const chartData = {
				labels,
				datasets: [
					{
						label: chart.title,
						data: chart.data,
						borderColor: chart.color,
						backgroundColor: `${chart.color}${Math.round(CHART_CONSTANTS.BACKGROUND_OPACITY * 255).toString(16).padStart(2, '0')}`,
						tension: CHART_CONSTANTS.LINE_TENSION,
						pointRadius: CHART_CONSTANTS.POINT_RADIUS,
						pointHoverRadius: CHART_CONSTANTS.POINT_HOVER_RADIUS,
						pointBackgroundColor: chart.color,
						pointBorderColor: chart.color,
						pointBorderWidth: CHART_CONSTANTS.POINT_BORDER_WIDTH,
						fill: false,
						spanGaps: true, // null 값이 있어도 선을 연결
					},
				],
			};

			const chartOptions = {
				maintainAspectRatio: false,
				responsive: true,
				plugins: {
					title: {
						display: true,
						text: chart.title,
						font: {
							size: CHART_CONSTANTS.TITLE_FONT_SIZE,
							weight: 'bold' as const,
						},
					},
					legend: {
						display: false,
					},
					tooltip: {
						enabled: true,
						mode: 'index' as const,
						intersect: false,
						callbacks: {
							label: function(context: any) {
								const value = context.parsed?.y;
								if (value === null || value === undefined) return '';
								return `${chart.title}: ${Number(value).toFixed(2)}`;
							},
							title: function(context: any) {
								const index = context[0]?.dataIndex;
								if (index === undefined) return '';
								const day = labels[index];
								// 해당 일자의 데이터 찾기
								const matchingItem = sortedData.find(item => {
									const itemDay = extractDayFromAnalysisDt(item.analysisDt);
									return itemDay === day;
								});
								if (matchingItem) {
									try {
										const date = new Date(matchingItem.analysisDt);
										return formatDate(date);
									} catch {
										return `${day}일`;
									}
								}
								return `${day}일`;
							}
						}
					}
				},
				scales: {
					x: {
						title: {
							display: true,
							text: '일자',
							font: { size: CHART_CONSTANTS.AXIS_FONT_SIZE }
						},
					ticks: {
						font: { size: CHART_CONSTANTS.TICK_FONT_SIZE },
						maxTicksLimit: 31, // 1일부터 31일까지 모두 표시
						stepSize: 1, // 모든 라벨 표시
					}
					},
					y: {
						beginAtZero: false,
						min: yMin,
						max: yMax,
						title: {
							display: true,
							text: '값',
							font: { size: CHART_CONSTANTS.AXIS_FONT_SIZE }
						},
						ticks: {
							font: { size: CHART_CONSTANTS.TICK_FONT_SIZE },
						}
					},
				},
			};

			return {
				chartData,
				chartOptions,
				index,
			};
		});
	}, [data]);

	if (!data || data.length === 0) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-gray-500">차트 데이터가 없습니다.</p>
			</div>
		);
	}

	return (
		<div className="h-full w-full overflow-auto">
			<div className="grid grid-cols-2 gap-4 p-2">
				{chartConfigs.map((config) => (
					<div key={`chart-${config.index}`} className="bg-white border border-gray-300 rounded shadow-sm p-3">
						<div style={{ height: CHART_CONSTANTS.CHART_HEIGHT }}>
							<Line data={config.chartData} options={config.chartOptions} />
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

